import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gasAdminMe, type ErrorResponse } from '@vn-propane/shared';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LanguageSelector } from '@/components/ui/language-selector';
import { ChevronLeft, CheckCircle } from 'lucide-react';

interface OAuthAccount {
  provider: string;
  provider_user_id: string;
  email: string | null;
  name: string | null;
  linked_at: string;
}

export default function AccountSettingsPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState<{ id: string; username: string; email: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [availableProviders, setAvailableProviders] = useState<string[]>([]);
  const [linkedAccounts, setLinkedAccounts] = useState<OAuthAccount[]>([]);
  const [isLinking, setIsLinking] = useState(false);

  // Handle OAuth callback query params
  useEffect(() => {
    const oauthLinked = searchParams.get('oauth_linked');
    const oauthError = searchParams.get('oauth_error');

    if (oauthLinked === 'true') {
      setSuccess(t('accountSettings.linkSuccess'));
      // Clear the query params
      setSearchParams({});
    } else if (oauthError) {
      setError(decodeURIComponent(oauthError));
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, t]);

  // Fetch current user and OAuth data
  useEffect(() => {
    async function fetchData() {
      try {
        // Get current user
        const userResult = await gasAdminMe();
        if (userResult.error) {
          const errorResponse = userResult.error as ErrorResponse;
          if (userResult.response?.status === 401) {
            navigate('/login');
            return;
          }
          setError(errorResponse.error?.message || 'Failed to load user');
          return;
        }
        if (userResult.data) {
          setUser(userResult.data.data);

          // Fetch available OAuth providers
          const apiUrl = import.meta.env.VITE_API_URL || '';
          const providersResponse = await fetch(`${apiUrl}/auth/oauth/providers`, {
            credentials: 'include',
          });
          if (providersResponse.ok) {
            const data = await providersResponse.json();
            setAvailableProviders(data.data?.providers || []);
          }

          // Fetch linked OAuth accounts
          const accountsResponse = await fetch(
            `${apiUrl}/auth/oauth/accounts?user_type=gas_admin&user_id=${userResult.data.data.id}`,
            { credentials: 'include' }
          );
          if (accountsResponse.ok) {
            const data = await accountsResponse.json();
            setLinkedAccounts(data.data || []);
          }
        }
      } catch (err) {
        setError('Failed to load account settings');
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [navigate]);

  const handleLinkGoogle = () => {
    if (!user) return;
    setIsLinking(true);
    const apiUrl = import.meta.env.VITE_API_URL || '';
    window.location.href = `${apiUrl}/auth/oauth/google/link?user_type=gas_admin&user_id=${user.id}`;
  };

  const handleUnlinkGoogle = async () => {
    if (!user) return;
    if (!confirm(t('accountSettings.confirmUnlink'))) return;

    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(
        `${apiUrl}/auth/oauth/google?user_type=gas_admin&user_id=${user.id}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (response.ok) {
        setLinkedAccounts((prev) => prev.filter((a) => a.provider !== 'google'));
      } else {
        setError(t('accountSettings.unlinkError'));
      }
    } catch {
      setError(t('accountSettings.unlinkError'));
    }
  };

  const isGoogleLinked = linkedAccounts.some((a) => a.provider === 'google');
  const googleAccount = linkedAccounts.find((a) => a.provider === 'google');

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted/40">
        <div className="animate-pulse">
          <div className="h-16 bg-background border-b" />
          <div className="max-w-2xl mx-auto p-4 md:p-8">
            <div className="h-8 bg-muted rounded w-1/4 mb-4" />
            <div className="h-64 bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/40">
      {/* Header */}
      <header className="h-14 md:h-16 bg-background border-b flex items-center px-4 md:px-6 justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            {t('buttons.back')}
          </button>
        </div>
        <LanguageSelector />
      </header>

      {/* Content */}
      <div className="max-w-2xl mx-auto p-4 md:p-8">
        <h1 className="text-2xl font-extrabold tracking-tight mb-2">{t('accountSettings.title')}</h1>
        <p className="text-muted-foreground mb-8">{t('accountSettings.description')}</p>

        {success && (
          <Alert className="mb-6 border-green-500 bg-green-50 dark:bg-green-950">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800 dark:text-green-200">{success}</AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Connected Accounts Section */}
        <div className="bg-background rounded-lg border p-6">
          <h2 className="text-lg font-semibold mb-2">{t('accountSettings.connectedAccounts')}</h2>
          <p className="text-sm text-muted-foreground mb-6">
            {t('accountSettings.connectedAccountsDescription')}
          </p>

          {/* Google */}
          {availableProviders.includes('google') && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 py-4 border-t">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white border flex items-center justify-center">
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                </div>
                <div>
                  <div className="font-medium">Google</div>
                  {isGoogleLinked ? (
                    <div className="text-sm text-muted-foreground">
                      {googleAccount?.email || googleAccount?.name || t('accountSettings.googleLinked')}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">{t('accountSettings.notLinked')}</div>
                  )}
                </div>
              </div>
              <div>
                {isGoogleLinked ? (
                  <Button variant="outline" size="sm" onClick={handleUnlinkGoogle}>
                    {t('accountSettings.unlinkAccount')}
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={handleLinkGoogle} disabled={isLinking}>
                    {t('accountSettings.linkAccount')}
                  </Button>
                )}
              </div>
            </div>
          )}

          {availableProviders.length === 0 && (
            <p className="text-sm text-muted-foreground py-4 border-t">
              No OAuth providers are configured.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
